import React from "react";
import * as Reinspect from "reinspect";
import {
  getKostrarapporttyper,
  getOmraader,
  requestReport,
  QueryData,
} from "../../data/api";
import "twin.macro";
import * as R from "remeda";
import SharedFylkeMultiNew from "@nvdb-rapport/shared/src/components/AreaSelect/FylkeMultiNew";
import KostraTypeSelect from "./KostraTypeSelect";
import { useInterval } from "@nvdb-rapport/shared/src/hooks/useInterval";
import { Just } from "purify-ts";
import PollDataView, { pollData } from "../polling";
import {
  formModelReducer,
  initialFormModel,
  setReportAction,
  setAppStatus,
  setKostratypeAction,
  setOmraaderAction,
  selectKostraRapportTypeAction,
  toggleSelectedFylkeAction,
  addErrorAction,
  ModelProp,
  DispatchProp,
  FormModel,
  updateVegsystemAction,
  updateSelectedKommuneAction,
  resetFormAction,
  FormAction,
  selectReadVersion,
} from "./update";

import Vegfilter from "@nvdb-rapport/shared/src/components/Vegfilter";
import KommuneSelectNew from "@nvdb-rapport/shared/src/components/AreaSelect/KommuneSelectNew";
import { ArticleContainer } from "@nvdb-rapport/shared/src/styled/Container";
import ErrorBox from "@nvdb-rapport/shared/src/components/ErrorBox";
import {
  ResetButton,
  SubmitButton,
} from "@nvdb-rapport/shared/src/styled/Button";
import Head from "@nvdb-rapport/shared/src/components/Head";
import { alertBeforeUnload } from "@nvdb-rapport/shared/src/utils/alertBeforeUnload";
import styled from "@emotion/styled";

function FormIndex({ dispatch, model }: DispatchProp & ModelProp) {
  // Polling action and interval
  useInterval(
    () => pollData({ dispatch, model }),
    model.appStatus.type === "FetchingReport" && model.reportData.isJust()
      ? 1000
      : null
  );

  React.useEffect(() => {
    if (model.appStatus.type === "FetchingReport") {
      window.addEventListener("beforeunload", alertBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", alertBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", alertBeforeUnload);
    };
  }, [model.appStatus]);

  React.useEffect(() => {
    // Set initial loading state on select fields
    dispatch(setKostratypeAction({ type: "LOADING" }));
    dispatch(setOmraaderAction({ type: "LOADING" }));

    // Fetch initial form values
    async function fetchData() {
      const kostraTypeData = await getKostrarapporttyper();
      dispatch(setKostratypeAction(kostraTypeData));
      const omraadeData = await getOmraader();
      dispatch(setOmraaderAction(omraadeData));
    }
    fetchData();
  }, []);

  return (
    <ArticleContainer style={{ marginTop: "2rem" }}>
      <Form model={model} dispatch={dispatch}></Form>
      {model.errors.length > 0 && (
        <ErrorBox errors={model.errors} siteTitle="Kostra" />
      )}
    </ArticleContainer>
  );
}

const formValidationConditions: ((m: FormModel) => boolean)[] = [
  m => m.selectedRapportType.value !== undefined,
  m => m.selectedRapportType.value?.implementert === true,
  m => m.selectedFylker.value.length > 0 || m.selectedKommuner.value.length > 0,
  m => m.vegsystem.isRight(),
];

function Form({ model, dispatch }: ModelProp & DispatchProp): JSX.Element {
  const formIsValid = R.allPass(model, formValidationConditions);
  return (
    <>
      <Head siteTitle="Kostra"></Head>
      <StyledForm>
        <KostraTypeSelect
          kostraTyper={model.kostraTyper}
          selectedRapportType={model.selectedRapportType}
          updateSelectedKostraRapportType={selected => {
            dispatch(selectKostraRapportTypeAction(selected));
          }}
        />
        <SharedFylkeMultiNew
          selectedFylker={model.selectedFylker}
          omraader={model.omraader}
          updateSelectedFylker={fylke =>
            dispatch(toggleSelectedFylkeAction(fylke))
          }
        />

        <KommuneSelectNew
          language="nb_NO"
          omraader={model.omraader}
          selectedFylker={model.selectedFylker}
          selectedKommuner={model.selectedKommuner}
          setSelectedKommuner={kommuner =>
            dispatch(updateSelectedKommuneAction(kommuner))
          }
        />

        <Vegfilter
          vegsystemInput={model.vegsystemInput}
          vegsystem={model.vegsystem}
          updateVegsystem={string => dispatch(updateVegsystemAction(string))}
          variant="filled"
        />
        <div>
          {model.reportData.caseOf({
            Nothing: () => <></>,
            Just: data => <PollDataView pollData={data} />,
          })}
        </div>
        {model.appStatus.type !== "FetchingReport" &&
          model.appStatus.type !== "FetchedReport" && (
            <SubmitButton
              disabled={!formIsValid}
              onClick={e => submitKostraRequest(e, model, dispatch)}
            >
              Hent data
            </SubmitButton>
          )}
        {(model.appStatus.type === "FetchedReport" ||
          model.appStatus.type === "Error") && (
          <ResetButton onClick={() => dispatch(resetFormAction())}>
            Tilbakestill
          </ResetButton>
        )}
      </StyledForm>
    </>
  );
}

async function submitKostraRequest(
  e: React.FormEvent,
  model: FormModel,
  dispatch: React.Dispatch<FormAction>
) {
  e.preventDefault();
  if (model.selectedRapportType !== undefined) {
    const queryData: QueryData = [
      model.selectedRapportType.value?.id!,
      {
        kommune:
          model.selectedKommuner.value.length > 0
            ? R.map(model.selectedKommuner.value, f => f.nummer)
            : undefined,
        fylke:
          model.selectedFylker.value.length > 0
            ? R.map(model.selectedFylker.value, f => f.nummer)
            : undefined,
        V4: model.selectedReadVersion?.value ?? true,
      },
    ];

    const requestData = await requestReport(queryData);

    requestData.caseOf({
      Left: e => {
        console.error("error requestData", e);
        dispatch(
          addErrorAction({
            title: "Feil ved innhenting av rapport",
            message: e,
          })
        );
      },
      Right: data => {
        dispatch(setReportAction(Just(data)));
        dispatch(setAppStatus({ type: "FetchingReport" }));
      },
    });
  }
}

function ProductionAppWrapper() {
  const [model, dispatch] = React.useReducer(
    formModelReducer,
    initialFormModel
  );
  return <FormIndex model={model} dispatch={dispatch} />;
}

function DevelopmentAppWrapper() {
  const [model, dispatch] = Reinspect.useReducer(
    formModelReducer,
    initialFormModel,
    state => state,
    "FORM"
  );
  return <FormIndex model={model} dispatch={dispatch} />;
}

export default function App() {
  if (process.env.NODE_ENV === "production") {
    return <ProductionAppWrapper />;
  } else {
    return <DevelopmentAppWrapper />;
  }
}

const StyledForm = styled.form`
  display: grid;
  flex-direction: column;
  width: 100%;
  grid-gap: 3rem;
  align-items: center;
`;
